import { createRootRouteWithContext, createRoute, createRouter, redirect } from "@tanstack/react-router";
import type { AuthContextProps } from "react-oidc-context";
import { hasAuthParams } from "react-oidc-context";
import { z } from "zod";

import { AppLayout } from "./app-layout";
import { CreateProjectStage1Page } from "./pages/create-project-page";
import { DetailedFileReportPage } from "./pages/reports-page/local-components/detailed-file-report";
import { StatisticsUsageSoftwarePage } from "./pages/reports-page/local-components/statistics-usage-software";
import { FilesystemPage } from "./pages/filesystem-page";
import { HelpPage } from "./pages/help-page";
import { ListOfExpertsPage } from "./pages/list-of-experts-page";
import { ProjectDashboardPage } from "./pages/project-dashboard-page";
import { ProjectsPage } from "./pages/projects-page";
import { ReportsPage } from "./pages/reports-page";

const DOCUMENT_TITLE_SUFFIX = " — Платформа 3Д";

export type GgeRouterContext = {
  getAuth: () => AuthContextProps;
};

const AUTH_WAIT_MS = 120_000;

const ensureKeycloakSession = async (getAuth: () => AuthContextProps): Promise<void> => {
  const deadline = Date.now() + AUTH_WAIT_MS;

  while (Date.now() < deadline) {
    const auth = getAuth();

    if (auth.isAuthenticated && auth.user?.access_token) {
      return;
    }

    if (auth.error) {
      await auth.signinRedirect();
      await new Promise(() => {});
    }

    const busy = auth.isLoading || auth.activeNavigator || (hasAuthParams() && !auth.isAuthenticated);

    if (!busy && (!auth.isAuthenticated || !auth.user?.access_token)) {
      await auth.signinRedirect();
      await new Promise(() => {});
    }

    await new Promise((r) => setTimeout(r, 24));
  }

  throw new Error("Истекло время ожидания входа в Keycloak");
};

const rootRoute = createRootRouteWithContext<GgeRouterContext>()({
  head: () => ({
    meta: [{ title: "Платформа 3Д" }],
  }),
  component: AppLayout,
  errorComponent: ({ error }) => {
    const message = error instanceof Error ? error.message : "Неизвестная ошибка";
    const stack = error instanceof Error ? error.stack : null;

    return (
      <div role="alert" className="text-type-error p-4">
        {message}
        {stack && <div className="text-type-minor mt-2 whitespace-pre-wrap">{stack}</div>}
      </div>
    );
  },
  pendingComponent: () => <div className="bg-neutrals-99 text-type-light flex h-screen w-full items-center justify-center">Вход…</div>,
  beforeLoad: async ({ context }) => {
    await ensureKeycloakSession(context.getAuth);
  },
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  head: () => ({
    meta: [{ title: `Проекты${DOCUMENT_TITLE_SUFFIX}` }],
  }),
  component: ProjectsPage,
});

const archivedProjectsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/archivedProjects",
  head: () => ({
    meta: [{ title: `Архив проектов${DOCUMENT_TITLE_SUFFIX}` }],
  }),
  component: () => <ProjectsPage isArchived />,
});

const projectDashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/project/$projectId/dashboard",
  component: ProjectDashboardPage,
});

const archiveProjectDashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/archiveProject/$projectId/dashboard",
  component: ProjectDashboardPage,
});

const projectRoleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/project/$projectId/role",
  component: ListOfExpertsPage,
});

const archiveProjectRoleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/archiveProject/$projectId/role",
  component: ListOfExpertsPage,
});

const createProjectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/add-project",
  head: () => ({
    meta: [{ title: `Добавление проекта${DOCUMENT_TITLE_SUFFIX}` }],
  }),
  component: CreateProjectStage1Page,
});

const projectFilesystemRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/project/$projectId/filesystem",
  component: FilesystemPage,
});

const projectFilesystemModelsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/project/$projectId/filesystem/models",
  component: () => <FilesystemPage fixedTab="models" hideTabs />,
});

const projectFilesystemDocsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/project/$projectId/filesystem/docs",
  component: () => <FilesystemPage fixedTab="docs" hideTabs />,
});

const projectFilesystemDrawingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/project/$projectId/filesystem/drawings",
  component: () => <FilesystemPage fixedTab="drawings" hideTabs />,
});

const archiveProjectFilesystemRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/archiveProject/$projectId/filesystem",
  component: FilesystemPage,
});

const archiveProjectFilesystemModelsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/archiveProject/$projectId/filesystem/models",
  component: () => <FilesystemPage fixedTab="models" hideTabs />,
});

const archiveProjectFilesystemDocsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/archiveProject/$projectId/filesystem/docs",
  component: () => <FilesystemPage fixedTab="docs" hideTabs />,
});

const archiveProjectFilesystemDrawingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/archiveProject/$projectId/filesystem/drawings",
  component: () => <FilesystemPage fixedTab="drawings" hideTabs />,
});

const helpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/help",
  head: () => ({
    meta: [{ title: `Помощь${DOCUMENT_TITLE_SUFFIX}` }],
  }),
  component: HelpPage,
});

const reportsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reports",
  head: () => ({
    meta: [{ title: `Отчёты${DOCUMENT_TITLE_SUFFIX}` }],
  }),
  component: ReportsPage,
});

const statisticsUsageSoftwareRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reports/statistics-usage-software",
  component: StatisticsUsageSoftwarePage,
});

const detailedFileReportSearchSchema = z.object({
  softwareName: z.string().optional(),
});

const detailedFileReportRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reports/detailed-file-report",
  validateSearch: detailedFileReportSearchSchema,
  component: DetailedFileReportPage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  archivedProjectsRoute,
  projectDashboardRoute,
  archiveProjectDashboardRoute,
  projectRoleRoute,
  archiveProjectRoleRoute,
  createProjectRoute,
  projectFilesystemRoute,
  projectFilesystemModelsRoute,
  projectFilesystemDocsRoute,
  projectFilesystemDrawingsRoute,
  archiveProjectFilesystemRoute,
  archiveProjectFilesystemModelsRoute,
  archiveProjectFilesystemDocsRoute,
  archiveProjectFilesystemDrawingsRoute,
  helpRoute,
  reportsRoute,
  statisticsUsageSoftwareRoute,
  detailedFileReportRoute,
]);

export const router = createRouter({
  routeTree,
  context: {
    getAuth: () => {
      throw new Error("Router: getAuth ещё не передан через RouterProvider");
    },
  },
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

export { redirect };
